import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardMedia,
  IconButton,
  CircularProgress,
  Paper,
  useTheme,
  Fade,
  Alert,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSnackbar } from "../../context/Snackbar";
import * as Sentry from "@sentry/react";
import axios from "axios";

const ImageUpload = ({
  onProcessImages,
  error,
  isMobile = false,
  API_BASE,
  selectedInterests,
  otherInterest,
  additionalNotes,
  triggerProcess,
  onOpenOptions,
}) => {
  const theme = useTheme();
  const MAX_FILES = 3;
  const { showWarning, showError } = useSnackbar();

  const [selectedFiles, setSelectedFiles] = useState([]); // all staged images
  const [filesToProcess, setFilesToProcess] = useState([]); // frozen batch for retry logic
  const [isLoading, setIsLoading] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set()); // Track which files are being processed

  // --- Live Camera State ---
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  /* -------------------------
     Helper: Add Files to State
  -------------------------- */
  const addFilesToState = (filesList) => {
    if (!filesList || filesList.length === 0) return;

    setUploadError(false);

    const nonProcessingCount = selectedFiles.filter(
      (f) => !processingIds.has(f.id)
    ).length;

    const remainingSlots = MAX_FILES - nonProcessingCount;
    if (remainingSlots <= 0) {
      showWarning(
        `Maximum ${MAX_FILES} images allowed. Please remove some images first.`
      );
      return;
    }

    const filesToAdd = Array.from(filesList).slice(0, remainingSlots);
    if (filesList.length > remainingSlots) {
      showWarning(
        `Only ${remainingSlots} more image(s) can be added. Maximum ${MAX_FILES} images allowed.`
      );
    }

    const fileArray = filesToAdd.map((file, index) => ({
      id: Date.now() + index,
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      previewUrl: URL.createObjectURL(file),
    }));

    setSelectedFiles((prev) => [...prev, ...fileArray]);
  };

  /* -------------------------
     Upload from Gallery
  -------------------------- */
  const stageImages = (event) => {
    addFilesToState(event.target.files);
    event.target.value = ""; // Reset input
  };

  /* -------------------------
     Live Web Camera Logic
  -------------------------- */
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraOpen(false);
  }, []);

  const startCamera = useCallback(async () => {
    setIsCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.error("Camera access denied or unavailable.", err);
      showError(
        "Camera access denied. Please allow camera permissions in your browser."
      );
      stopCamera();
    }
  }, [facingMode, showError, stopCamera]);

  const toggleFacingMode = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    if (isCameraOpen) {
      stopCamera();
      setTimeout(() => startCamera(), 100);
    }
  };

  const captureImage = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `capture_${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            addFilesToState([file]);
          }
          stopCamera();
        },
        "image/jpeg",
        0.9
      );
    }
  };

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  /* -------------------------
     Remove file
  -------------------------- */
  const removeFile = (fileId) => {
    if (processingIds.has(fileId)) return;

    setSelectedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove && fileToRemove.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  };

  /* -------------------------
     Clear All
  -------------------------- */
  const clearAllFiles = () => {
    if (processingIds.size > 0) return;

    selectedFiles.forEach((file) => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
    setSelectedFiles([]);
    setUploadError(false);
  };

  /* -------------------------
     Process Images
  -------------------------- */
  const processSelectedImages = async () => {
    const toUpload =
      uploadError && filesToProcess.length > 0
        ? filesToProcess
        : selectedFiles.filter((f) => !processingIds.has(f.id));

    if (toUpload.length === 0) return;

    const uploadIds = new Set(toUpload.map((f) => f.id));
    setProcessingIds(uploadIds);
    setFilesToProcess([...toUpload]);
    setIsLoading(true);
    setUploadError(false);

    try {
      const formData = new FormData();
      toUpload.forEach((file, index) =>
        formData.append(`image${index}`, file.file)
      );
      if (selectedInterests && selectedInterests.length > 0) {
        formData.append("interests", JSON.stringify(selectedInterests));
      }
      if (otherInterest) {
        formData.append("otherInterest", otherInterest);
      }
      if (additionalNotes) {
        formData.append("additionalNotes", additionalNotes);
      }

      const res = await axios.post(
        `${API_BASE}/parse-image-save-cards`,
        formData,
        {
          withCredentials: true,
        }
      );

      if (!res?.data?.success && !res?.data?.cards) {
        throw new Error("Failed to process images");
      }

      // 🛠️ DATA SANITIZATION FIX: Ensure fields that should be strings are strictly strings.
      // If AI accidentally returns an array (like ["email1@test.com", "email2@test.com"]), 
      // join them into a single string so .trim() won't crash the frontend UI.
      const safeString = (val) => {
        if (Array.isArray(val)) return val.filter(Boolean).join(", ");
        if (typeof val === "string") return val;
        return "";
      };

      const rawCards = res.data?.cards || [];
      const sanitizedCards = rawCards.map((card) => ({
        ...card,
        name: safeString(card.name),
        email: safeString(card.email), // Prevents card.email.trim is not a function
        title: safeString(card.title),
        company: safeString(card.company),
        website: safeString(card.website),
        address: safeString(card.address),
        // Note: phone is often expected to be an array in the UI. We'll leave it alone.
      }));

      onProcessImages(sanitizedCards);

      const processedIds = new Set(toUpload.map((f) => f.id));
      setSelectedFiles((prev) => prev.filter((f) => !processedIds.has(f.id)));

      setFilesToProcess([]);
      setProcessingIds(new Set());
    } catch (e) {
      console.error(e.message || "Failed to process images");
      setUploadError(true);
      setProcessingIds(new Set());

      if (e.response && e.response.status >= 500) {
        Sentry.captureException(e, {
          tags: { section: "image-upload", source: "parse-image-save-cards" },
          extra: {
            status: e.response.status,
            response: e.response.data,
          },
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (triggerProcess > 0) {
      processSelectedImages();
    }
  }, [triggerProcess]);

  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl);
        }
      });
    };
    // eslint-disable-next-line
  }, []);

  const activeCount = selectedFiles.filter(
    (f) => !processingIds.has(f.id)
  ).length;

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        maxWidth: 620,
        mx: "auto",
        minHeight: 345,
        backgroundColor: "#F9FAFB",
        borderRadius: 3,
        display: "flex",
        flexDirection: "column",
        border: `1.5px dashed ${theme.palette.divider}`,
        p: { xs: 3, md: 4 },
        mb: 5,
      }}
    >
      <Stack alignItems="center" spacing={2}>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{ fontWeight: 600, color: "#1e293b", textAlign: "center" }}
        >
          Upload Business Card Images
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 2 }}
        >
          Select up to <b>{MAX_FILES}</b> images, preview them, then process
          with AI
        </Typography>

        {/* LIVE CAMERA PREVIEW SECTION */}
        {isCameraOpen ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "4/3",
                backgroundColor: "#000",
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                mb: 2,
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              <IconButton
                onClick={stopCamera}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={captureImage}
                startIcon={<PhotoCameraIcon />}
                size="large"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  backgroundColor: "#10B981",
                  "&:hover": { backgroundColor: "#059669" },
                }}
              >
                Take Photo
              </Button>
              {navigator.mediaDevices?.enumerateDevices && (
                <IconButton
                  onClick={toggleFacingMode}
                  title="Switch Camera"
                  sx={{
                    backgroundColor: "#E5E7EB",
                    "&:hover": { backgroundColor: "#D1D5DB" },
                  }}
                >
                  <CameraswitchIcon />
                </IconButton>
              )}
            </Stack>
          </Box>
        ) : (
          /* BUTTONS SECTION */
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              mb: selectedFiles.length > 0 ? 1 : 0,
              width: "100%",
              justifyContent: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={startCamera}
              startIcon={<PhotoCameraIcon />}
              size={isMobile ? "medium" : "large"}
              disabled={activeCount >= MAX_FILES}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                backgroundColor: "#10B981",
                "&:hover": { backgroundColor: "#059669" },
              }}
            >
              Click Image
            </Button>

            <Button
              variant="contained"
              component="label"
              startIcon={<ImageIcon />}
              size={isMobile ? "medium" : "large"}
              disabled={activeCount >= MAX_FILES}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                backgroundColor: "#6366F1",
                "&:hover": { backgroundColor: "#4F46E5" },
              }}
            >
              Upload Image
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={stageImages}
                disabled={activeCount >= MAX_FILES}
              />
            </Button>
          </Stack>
        )}

        {activeCount >= MAX_FILES && !isCameraOpen && (
          <Typography variant="caption" color="error">
            Maximum images reached.
          </Typography>
        )}

        {/* Selected Files Display */}
        {selectedFiles.length > 0 && !isCameraOpen && (
          <Box sx={{ width: "100%", mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ color: "#374151" }}
              >
                Selected ({selectedFiles.length})
                {processingIds.size > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="primary"
                    sx={{ ml: 1 }}
                  >
                    ({processingIds.size} processing)
                  </Typography>
                )}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={clearAllFiles}
                disabled={processingIds.size > 0}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Clear All
              </Button>
            </Box>

            {/* Images List */}
            <Grid container spacing={2}>
              {selectedFiles.map((fileObj) => {
                const fileName = fileObj.name;
                const fileExtension = fileName.substring(
                  fileName.lastIndexOf(".")
                );
                const baseName = fileName.substring(
                  0,
                  fileName.lastIndexOf(".")
                );
                const isProcessing = processingIds.has(fileObj.id);

                return (
                  <Grid item xs={12} sm={6} md={4} key={fileObj.id}>
                    <Card
                      elevation={2}
                      sx={{
                        position: "relative",
                        borderRadius: 3,
                        minHeight: 168,
                        bgcolor: "#fff",
                        boxShadow: "0 2px 14px rgba(99,102,241,0.08)",
                        transition: "box-shadow 0.2s",
                        opacity: isProcessing ? 0.6 : 1,
                        border: isProcessing ? "2px solid #6366F1" : "none",
                        "&:hover": {
                          boxShadow: "0 6px 20px rgba(99,102,241,0.18)",
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="120"
                        image={fileObj.previewUrl}
                        alt={fileName}
                        sx={{
                          objectFit: "cover",
                          borderRadius: "10px 10px 0 0",
                          background: "#f3f4f6",
                        }}
                      />
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          bgcolor: "#fff",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                          "&:hover": {
                            bgcolor: "#fee2e2",
                          },
                        }}
                        size="small"
                        onClick={() => removeFile(fileObj.id)}
                        disabled={isProcessing}
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                      <Box sx={{ p: 1.2, pb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: 500,
                            color: "#374151",
                          }}
                        >
                          {baseName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75em" }}
                        >
                          {fileExtension} &bull;{" "}
                          {(fileObj.size / 1024).toFixed(1)} KB
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* Process Button */}
            {!uploadError && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SendIcon />}
                  onClick={onOpenOptions}
                  disabled={isLoading || activeCount === 0}
                  size={isMobile ? "medium" : "large"}
                  sx={{ fontWeight: 600, borderRadius: 2, px: 3 }}
                >
                  Process {activeCount} Image
                  {activeCount > 1 ? "s" : ""} with AI
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Loading State */}
        <Fade in={isLoading} unmountOnExit>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 3,
              mb: 1,
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Processing {filesToProcess.length} image
              {filesToProcess.length === 1 ? "" : "s"} with AI...
            </Typography>
          </Box>
        </Fade>

        {/* Error State with Retry */}
        <Fade in={uploadError && !isLoading} unmountOnExit>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 3,
              mb: 1,
              width: "100%",
            }}
          >
            <Alert
              severity="error"
              sx={{
                width: "100%",
                mb: 2,
                borderRadius: 2,
              }}
            >
              Failed to process media
            </Alert>
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={processSelectedImages}
              size={isMobile ? "medium" : "large"}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                backgroundColor: "#6366F1",
                "&:hover": { backgroundColor: "#4F46E5" },
              }}
            >
              Retry Upload
            </Button>
          </Box>
        </Fade>
      </Stack>
    </Paper>
  );
};

export default ImageUpload;